import React, { useState } from 'react';
import { useDivisionRoles, useDeleteDivisionRole } from '../../hooks/use-division-roles';
import { DivisionRole } from '../../types';
import { Button } from '../ui/button';
import { formatDate } from '../../utils';

interface DivisionRoleTableProps {
  divisionId: string;
  onEditRole: (role: DivisionRole) => void;
}

export function DivisionRoleTable({ divisionId, onEditRole }: DivisionRoleTableProps) {
  const { data: rolesResponse, isLoading, error } = useDivisionRoles(divisionId);
  const deleteDivisionRole = useDeleteDivisionRole();
  const [deletingRoleId, setDeletingRoleId] = useState<string | null>(null);

  const roles = rolesResponse?.roles || [];

  const handleDeleteRole = async (role: DivisionRole) => {
    if (!window.confirm(`Are you sure you want to delete the role "${role.name}"? This action cannot be undone.`)) {
      return;
    }

    setDeletingRoleId(role.id);
    try {
      await deleteDivisionRole.mutateAsync({
        divisionId,
        roleId: role.id,
      });
    } catch (error) {
      console.error('Failed to delete role:', error);
      // Error handling is typically done via react-query's error handling
    } finally {
      setDeletingRoleId(null);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 dark:bg-red-950 border border-red-200 dark:border-red-800 rounded-lg p-4">
        <div className="flex">
          <div className="text-red-400">
            <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
            </svg>
          </div>
          <div className="ml-3">
            <h3 className="text-sm font-medium text-red-800">
              Error loading division roles
            </h3>
            <div className="mt-2 text-sm text-red-700 dark:text-red-400">
              <p>Failed to load roles for this division. Please try refreshing the page.</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (roles.length === 0) {
    return (
      <div className="text-center py-8 text-secondary-600 dark:text-secondary-400 bg-secondary-50 dark:bg-secondary-900 rounded-lg border">
        <div className="mb-4">
          <svg className="mx-auto h-12 w-12 text-secondary-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
          </svg>
        </div>
        <h3 className="text-lg font-medium text-secondary-900 dark:text-white mb-2">No roles found</h3>
        <p className="text-secondary-600 dark:text-secondary-400 mb-4">
          This division doesn't have any custom roles yet.
        </p>
      </div>
    );
  }

  return (
    <div className="overflow-hidden shadow ring-1 ring-black ring-opacity-5 md:rounded-lg">
      <table className="min-w-full divide-y divide-secondary-300">
        <thead className="bg-secondary-50 dark:bg-secondary-900">
          <tr>
            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-secondary-500 dark:text-secondary-400 uppercase tracking-wider">
              Role Name
            </th>
            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-secondary-500 dark:text-secondary-400 uppercase tracking-wider">
              Self-Registration
            </th>
            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-secondary-500 dark:text-secondary-400 uppercase tracking-wider">
              Access
            </th>
            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-secondary-500 dark:text-secondary-400 uppercase tracking-wider">
              Created
            </th>
            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-secondary-500 dark:text-secondary-400 uppercase tracking-wider">
              Last Modified
            </th>
            <th scope="col" className="relative px-6 py-3">
              <span className="sr-only">Actions</span>
            </th>
          </tr>
        </thead>
        <tbody className="bg-white dark:bg-secondary-800 divide-y divide-secondary-200">
          {roles.map((role) => (
            <tr key={role.id} className="hover:bg-secondary-50 dark:hover:bg-secondary-700">
              <td className="px-6 py-4 whitespace-nowrap">
                <div className="flex items-center">
                  <div className="flex-shrink-0 h-8 w-8">
                    <div className="h-8 w-8 rounded-full bg-primary-100 flex items-center justify-center">
                      <svg className="h-4 w-4 text-primary-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                      </svg>
                    </div>
                  </div>
                  <div className="ml-4">
                    <div className="text-sm font-medium text-secondary-900 dark:text-white">
                      {role.name}
                    </div>
                  </div>
                </div>
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm">
                {role.includeInRegistration ? (
                  <span className="inline-flex rounded-full bg-green-100 dark:bg-green-900 px-2 text-xs font-semibold leading-5 text-green-800 dark:text-green-200">
                    Included
                  </span>
                ) : (
                  <span className="inline-flex rounded-full bg-secondary-100 dark:bg-secondary-700 px-2 text-xs font-semibold leading-5 text-secondary-600 dark:text-secondary-300">
                    Excluded
                  </span>
                )}
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm">
                {!role.isDivisionAdmin && !role.isWorkflowAdmin ? (
                  <span className="text-secondary-400 dark:text-secondary-500">&mdash;</span>
                ) : (
                  <div className="flex flex-wrap gap-1">
                    {role.isDivisionAdmin && (
                      <span className="inline-flex rounded-full bg-purple-100 dark:bg-purple-900 px-2 text-xs font-semibold leading-5 text-purple-800 dark:text-purple-200">
                        Division Admin
                      </span>
                    )}
                    {role.isWorkflowAdmin && (
                      <span className="inline-flex rounded-full bg-blue-100 dark:bg-blue-900 px-2 text-xs font-semibold leading-5 text-blue-800 dark:text-blue-200">
                        Workflow Admin
                      </span>
                    )}
                  </div>
                )}
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm text-secondary-500 dark:text-secondary-400">
                {formatDate(role.createdAt)}
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm text-secondary-500 dark:text-secondary-400">
                {formatDate(role.updatedAt)}
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                <div className="flex items-center justify-end space-x-2">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => onEditRole(role)}
                    className="text-primary-600 hover:text-primary-900"
                  >
                    Edit
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleDeleteRole(role)}
                    disabled={deletingRoleId === role.id}
                    className="text-red-600 dark:text-red-400 hover:text-red-900"
                  >
                    {deletingRoleId === role.id ? (
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-red-600"></div>
                    ) : (
                      'Delete'
                    )}
                  </Button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
